import { Link, Head, usePage } from '@inertiajs/react'
import { ArrowRight, Building2, Calendar, Ticket, Sparkles, Store } from 'lucide-react'
import { VerifiedBadge } from '@/components/VerifiedBadge'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import EventCard from '@/components/EventCard'
import HeroEventCard from '@/components/home/HeroEventCard'
import type { EventListItem, FeaturedOrganization, SharedProps } from '@/types'

type HomeProps = {
  featured_events: EventListItem[]
  featured_organizations: FeaturedOrganization[]
}

const DOORS = [
  {
    icon: Ticket,
    audience: 'Attendees',
    title: 'Find a cause to celebrate.',
    body: 'Discover galas, buy tickets, and give — all in a few taps.',
    cta: 'Discover events',
    href: '/events',
  },
  {
    icon: Sparkles,
    audience: 'Nonprofits',
    title: 'Host an extraordinary event.',
    body: 'Native ticketing, donations, and a dashboard built for galas.',
    cta: 'See what you get',
    href: '/organizations/sign_up',
  },
  {
    icon: Store,
    audience: 'Vendors',
    title: 'List your services.',
    body: 'Get in front of nonprofits planning galas. Free to list.',
    cta: 'Browse directory',
    href: '/services',
  },
]

export default function Home({ featured_events, featured_organizations }: HomeProps) {
  const { app_url } = usePage<SharedProps>().props
  const ogImage = `${app_url}/meta.png`

  return (
    <>
      <Head title="Gala Season — Discover Charitable Events">
        <meta head-key="description" name="description" content="Discover, attend, and support extraordinary charitable gala events. Find featured galas and organizations making a difference." />
        <meta head-key="og:title" property="og:title" content="Gala Season — Discover Charitable Events" />
        <meta head-key="og:description" property="og:description" content="Discover, attend, and support extraordinary charitable gala events." />
        <meta head-key="og:type" property="og:type" content="website" />
        <meta head-key="og:url" property="og:url" content={app_url} />
        <meta head-key="og:image" property="og:image" content={ogImage} />
        <meta head-key="twitter:card" name="twitter:card" content="summary_large_image" />
        <meta head-key="twitter:title" name="twitter:title" content="Gala Season — Discover Charitable Events" />
        <meta head-key="twitter:description" name="twitter:description" content="Discover, attend, and support extraordinary charitable gala events." />
        <meta head-key="twitter:image" name="twitter:image" content={ogImage} />
      </Head>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 z-0" aria-hidden="true">
          {/* Warm amber wash bleeding down from the top, fading into the page background */}
          <div className="absolute inset-x-0 top-0 h-[140%] bg-[linear-gradient(to_bottom,oklch(0.85_0.11_62/0.55)_0%,oklch(0.91_0.07_72/0.28)_28%,transparent_62%)]" />
          {/* Concentrated peach glow behind the hero card (top-right) */}
          <div className="absolute -top-1/4 -right-[8%] h-[95%] w-[62%] rounded-full bg-[radial-gradient(circle,oklch(0.80_0.15_55/0.5)_0%,transparent_65%)] blur-2xl" />
          {/* Soft golden glow lower-left for balance */}
          <div className="absolute top-1/3 -left-[10%] h-[70%] w-[45%] rounded-full bg-[radial-gradient(circle,oklch(0.88_0.10_85/0.3)_0%,transparent_70%)] blur-3xl" />
        </div>
        <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:gap-8 lg:px-8 lg:py-24">
          <div>
            <p className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-primary">
              <span className="h-px w-6 bg-primary/40" />
              A new home for nonprofit events
            </p>
            <h1 className="mt-5 text-5xl font-semibold tracking-tight text-balance sm:text-6xl">
              Where Events meet{' '}
              <span className="font-serif italic text-primary underline decoration-primary/30 decoration-4 underline-offset-4">
                their cause.
              </span>
            </h1>
            <p className="mt-6 max-w-[46ch] text-lg text-foreground text-pretty">
              The home of charitable events. Where causes and community meet.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Button size="lg" asChild>
                <Link href="/organizations/sign_up" className="gap-1.5">
                  Get Started <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link href="/events">Discover events</Link>
              </Button>
            </div>
          </div>
          <div className="lg:pl-8">
            <HeroEventCard />
          </div>
        </div>
      </section>

      {/* Hand-picked events */}
      {featured_events.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between">
            <h2 className="max-w-[18ch] text-3xl font-semibold tracking-tight text-balance sm:text-4xl">
              Hand-picked events you won't want to miss.
            </h2>
            <Button variant="ghost" size="sm" asChild className="shrink-0">
              <Link href="/events" className="gap-1">
                View all events <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {featured_events.map((event) => (
              <Link key={event.id} href={`/events/${event.slug}`} className="block">
                <EventCard event={event} showOrganization />
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Organizations Making a Difference */}
      {featured_organizations.length > 0 && (
        <section className="bg-muted/30">
          <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
            <div className="text-center">
              <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">Organizations Making a Difference</h2>
              <p className="mt-2 text-base text-muted-foreground">Meet some of the organizations on Gala Season.</p>
            </div>
            <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {featured_organizations.map((org) => (
                <Link key={org.id} href={`/o/${org.slug}`} className="group block">
                  <div className="h-full rounded-2xl bg-card p-6 shadow-sm ring-1 ring-black/5 transition-all duration-200 hover:shadow-lg hover:ring-primary/20">
                    <div className="flex items-start gap-4">
                      {org.logo_url ? (
                        <img src={org.logo_url} alt="" width={48} height={48} loading="lazy" decoding="async" className="size-12 shrink-0 rounded-xl object-cover" />
                      ) : (
                        <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                          <Building2 className="size-5 text-primary" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <h3 className="flex items-center gap-1.5 font-semibold leading-snug">{org.name}{org.verified && <VerifiedBadge className="size-3.5" />}</h3>
                        {org.primary_cause && (
                          <Badge variant="secondary" className="mt-1.5 bg-primary/10 text-primary hover:bg-primary/10">{org.primary_cause}</Badge>
                        )}
                      </div>
                    </div>
                    {org.description && (
                      <p className="mt-3 text-base text-muted-foreground line-clamp-2">{org.description}</p>
                    )}
                    <div className="mt-4 flex items-center gap-1.5 text-base text-muted-foreground">
                      <Calendar className="size-3.5" />
                      <span>{org.events_count} upcoming {org.events_count === 1 ? 'event' : 'events'}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Three doors */}
      <section className="bg-muted/30">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-sm font-semibold uppercase tracking-wide text-primary">Three doors, same mission</p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight text-balance sm:text-4xl">
              However you show up, you belong here.
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
            {DOORS.map((door) => (
              <div key={door.audience} className="flex h-full flex-col rounded-2xl border border-black/10 bg-card p-6 shadow-xs">
                <span className="flex size-11 items-center justify-center rounded-xl bg-primary/10">
                  <door.icon className="size-5 text-primary" />
                </span>
                <p className="mt-5 text-sm font-semibold uppercase tracking-wide text-muted-foreground">{door.audience}</p>
                <h3 className="mt-1 text-xl font-semibold tracking-tight">{door.title}</h3>
                <p className="mt-2 flex-1 text-base text-muted-foreground text-pretty">{door.body}</p>
                <Link href={door.href} className="mt-5 inline-flex items-center gap-1 text-base font-medium text-primary hover:underline">
                  {door.cta} <ArrowRight className="size-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="rounded-2xl bg-linear-to-br from-[#F3722B] to-[#F7961E] p-8 text-center sm:p-12">
          <h2 className="text-3xl font-semibold tracking-tight text-white sm:text-4xl">Ready to host your own event?</h2>
          <p className="mt-2 text-white/80 font-medium">Create your organization profile and start planning your next charitable event.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link href="/organizations/sign_up" className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg bg-white px-4 text-base font-semibold text-primary transition-all hover:bg-white/90">
              Get Started
            </Link>
            <Button size="lg" variant="outline" className="border-white/30 bg-white/10 font-semibold text-white backdrop-blur-sm hover:bg-white/20 hover:text-white" asChild>
              <Link href="/events">Browse Events</Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  )
}
